import { MobileStudio } from './mobile/MobileStudio.js';
import { useIsMobile } from '../lib/use-mobile.js';
import { useEffect, useMemo, useRef, type ReactNode } from 'react';

import type { Project } from '@ai-studio/shared';

import { getView, type PrismMode } from '../engine/prism.js';
import { eventSocket } from '../lib/ws.js';
import { startFollowAssistant } from '../lib/follow-assistant.js';
import { startConsoleSync } from '../lib/console-sync.js';
import { startPlayTraceSync } from '../lib/play-trace-sync.js';
import { startConsoleBehaviours } from '../lib/console-behaviours.js';
import { useAgents } from '../state/agents.js';
import { useConsole } from '../state/console.js';
import { useModel } from '../state/model.js';
import { usePixel } from '../state/pixel.js';
import { useSound } from '../state/sound.js';
import { useGraph } from '../state/graph.js';
import { useVideo } from '../state/video.js';
import { usePlay } from '../state/play.js';
import { filesOfKind, useProjectFiles } from '../state/project-files.js';
import { useProviders } from '../state/providers.js';
import { useScene } from '../state/scene.js';
import { useTeam } from '../state/team.js';
import { DOCUMENT_PANELS, useLayout } from '../state/layout.js';
import { useWorkspace, type WorkspaceView } from '../state/workspace.js';
import { openPanels, primaryGroup, visiblePanels } from '../lib/dock-model.js';
import { clearOverlay } from '../engine/host.js';
import { GenerationPrompt } from './generation/GenerationPrompt.js';
import { AiPanel } from './AiPanel.js';
import { Board } from './Board.js';
import { CodeTab } from './CodeTab.js';
import { CommandPalette, openSearch } from './CommandPalette.js';
import { HistoryPanel } from './HistoryPanel.js';
import { BuildWindow } from './build-window/BuildWindow.js';
import { ParticleEditor } from './particles/ParticleEditor.js';
import { UvEditor } from './uv/UvEditor.js';
import { ModelTab } from './model/ModelTab.js';
import { PieMenuHost, QuickFavoritesHost } from './PieMenu.js';
import { FrameDebuggerPanel } from './profiler/FrameDebuggerPanel.js';
import { ProfilerPanel } from './profiler/ProfilerPanel.js';
import { SoundTab } from './sound/SoundTab.js';
import { GraphPanel } from './graph/GraphPanel.js';
import { ProcgenPanel } from './procgen/ProcgenPanel.js';
import { VideoTab } from './video/VideoStudio.js';
import { PixelTab } from './pixel/PixelTab.js';
import { PlayTab } from './PlayTab.js';
import { SceneTab } from './scene/SceneTab.js';
import { SettingsView } from './SettingsView.js';
import { ShortcutConflictPrompt, ShortcutHelperBar } from './ShortcutPrompts.js';
import { setFocusedPanel, useShellCommands } from './shell-setup.js';
import { TopBar } from './TopBar.js';
import { DockLayout, type DockPanelDef } from './ui/layout.js';
import { WEBSITE } from '../lib/edition.js';
import { PublishPanel, WebsiteNotice } from './website/PublishPanel.js';
import { useLocalGen } from '../state/local-gen.js';

/** Open the start scene (or first scene) and first model of a project. */
function useAutoOpenDocuments(projectId: number | null) {
  const entries = useProjectFiles((s) => s.entries);
  const filesFor = useProjectFiles((s) => s.projectId);
  const startScene = useProjectFiles((s) => s.startScene);
  const opened = useRef<number | null>(null);

  // Whether local generation is enabled (its buttons open its settings until it is).
  useEffect(() => {
    void useLocalGen.getState().load();
  }, []);

  useEffect(() => {
    if (
      !projectId ||
      filesFor !== projectId ||
      opened.current === projectId ||
      entries.length === 0
    )
      return;
    opened.current = projectId;
    const scenes = filesOfKind(entries, 'scene');
    const scene = startScene && scenes.includes(startScene) ? startScene : scenes[0];
    if (scene && !useScene.getState().path) void useScene.getState().open(scene);
    const model = filesOfKind(entries, 'model')[0];
    if (model && !useModel.getState().path) void useModel.getState().open(model);
  }, [projectId, filesFor, entries, startScene]);
}

/** The editor: top bar and the dock layout of views and tool panels. */
export function Workspace({ project }: { project: Project }) {
  const view = useWorkspace((s) => s.view);
  const projectId = useWorkspace((s) => s.projectId);
  const dock = useLayout((s) => s.dock);
  const setDock = useLayout((s) => s.set);
  const connectAgents = useAgents((s) => s.connect);
  const loadProviders = useProviders((s) => s.load);
  const loadMembers = useTeam((s) => s.load);
  const loadCatalog = useTeam((s) => s.loadCatalog);
  const resetTeam = useTeam((s) => s.reset);
  const clearActivity = useAgents((s) => s.clearActivity);

  useShellCommands();
  const mobile = useIsMobile();

  useEffect(() => connectAgents(), [connectAgents]);
  useEffect(
    () =>
      eventSocket.subscribe((event) => {
        if (event.type === 'editor.play' && event.projectId === projectId)
          usePlay.getState().play();
      }),
    [projectId],
  );

  // Loaded once here so the roster's provider and key pickers are populated on
  // first paint rather than only after the Providers panel has been opened.
  useEffect(() => {
    void loadProviders();
  }, [loadProviders]);

  /**
   * The roster is loaded on project open rather than by the Team panel: the
   * AI dock names the Assistant and resolves every activity entry to a member.
   */
  useEffect(() => {
    if (!projectId) {
      resetTeam();
      return;
    }
    void loadCatalog();
    void loadMembers(projectId);
  }, [projectId, loadCatalog, loadMembers, resetTeam]);

  // The feed belongs to whichever project is open.
  useEffect(() => {
    clearActivity();
    eventSocket.setProject(projectId);
    if (projectId) void useAgents.getState().loadTranscript(projectId);
  }, [projectId, clearActivity]);

  // Project files and documents follow the open project.
  useEffect(() => {
    useScene.getState().close();
    useModel.getState().close();
    usePixel.getState().close();
    useSound.getState().close();
    useVideo.getState().close();
    usePlay.getState().stop();
    clearOverlay();
    useConsole.getState().setProject(projectId);
    if (projectId) void useProjectFiles.getState().load(projectId);
    else useProjectFiles.getState().reset();
  }, [projectId]);

  useEffect(() => projectId ? startConsoleSync(projectId) : undefined, [projectId]);
  // Play window game trace → server (assistant read_game_trace) + live game_state answers.
  useEffect(() => (projectId ? startPlayTraceSync(projectId) : undefined), [projectId]);
  // Follow assistant: show what the AI does, in this project's tab only.
  useEffect(() => (projectId ? startFollowAssistant(projectId) : undefined), [projectId]);
  // Console Error Pause / Clear on Play / Clear on Build.
  useEffect(() => startConsoleBehaviours(), []);

  useAutoOpenDocuments(projectId);

  // Files changed on disk (the AI, git, another editor): reload what is open
  // and hot-reload every live engine view.
  useEffect(
    () =>
      eventSocket.subscribe((event) => {
        if (event.type !== 'file.changed' || event.projectId !== useWorkspace.getState().projectId)
          return;
        const path = event.path;
        // Problems reported against this file may be fixed now; they come back if not.
        useConsole.getState().resolveFile(path);
        const scenePath = useScene.getState().path;
        const modelPath = useModel.getState().path;
        if (path === scenePath) void useScene.getState().externalChange(path);
        if (path === modelPath) void useModel.getState().externalChange(path);
        else if (path.endsWith('.model.json')) void useModel.getState().followExternal(path);
        if (path === usePixel.getState().path) void usePixel.getState().externalChange(path);
        useSound.getState().fileChanged(path);
        useGraph.getState().fileChanged(path);
        if (path === useVideo.getState().path) void useVideo.getState().refresh();
        void useWorkspace.getState().reloadFromDisk(path);
        useProjectFiles.getState().refreshSoon();
        for (const mode of ['scene', 'model', 'play'] as PrismMode[]) {
          // The document stores resync their own file only when it really changed.
          if ((mode === 'scene' && path === scenePath) || (mode === 'model' && path === modelPath))
            continue;
          void getView(mode)?.fileChanged(path);
        }
        if (path === 'prism.project.json' && projectId)
          void useProjectFiles.getState().load(projectId);
      }),
    [projectId],
  );

  // Switching view (top bar, Ctrl+1..8, palette) shows that view wherever it is docked.
  useEffect(() => {
    if (!visiblePanels(useLayout.getState().dock).includes(view)) useLayout.getState().show(view);
  }, [view]);

  // Closing the focused view hands focus to what the main area shows next.
  useEffect(() => {
    if (openPanels(dock).includes(view)) return;
    const next = primaryGroup(dock)?.active ?? visiblePanels(dock).find((p) => DOCUMENT_PANELS.includes(p));
    if (next) useWorkspace.getState().setView(next as WorkspaceView);
  }, [dock, view]);

  const panels = useMemo<DockPanelDef[]>(
    () => [
      { id: 'scene', title: 'Scene', icon: 'scene', document: true, closable: false, minSize: 320, render: () => <DocumentHost><ActiveTab id="scene" /></DocumentHost> },
      { id: 'model', title: 'Model', icon: 'model', document: true, minSize: 320, render: () => <DocumentHost><ActiveTab id="model" /></DocumentHost> },
      { id: 'pixel', title: 'Pixel', icon: 'pixel', document: true, minSize: 320, render: () => <DocumentHost><ActiveTab id="pixel" /></DocumentHost> },
      { id: 'sound', title: 'Sound', icon: 'audio', document: true, minSize: 320, render: () => <DocumentHost><ActiveTab id="sound" /></DocumentHost> },
      { id: 'video', title: 'Video', icon: 'camera', document: true, minSize: 480, render: () => (WEBSITE ? <WebsiteNotice what="Video" /> : <DocumentHost><ActiveTab id="video" /></DocumentHost>) },
      // Always mounted: its engine keeps the scene loaded and in sync, so Play starts instantly.
      { id: 'play', title: 'Play', icon: 'playOutline', document: true, alwaysMounted: true, minSize: 280, render: () => <DocumentHost><PlayTab /></DocumentHost> },
      { id: 'code', title: 'Code', icon: 'code', document: true, minSize: 320, render: () => <DocumentHost><CodeTab projectId={project.id} projectName={project.name} /></DocumentHost> },
      { id: 'board', title: 'Board', icon: 'board', document: true, tearOff: true, render: () => (WEBSITE ? <WebsiteNotice what="The task board" /> : <PagePanel label="Task Board"><Board /></PagePanel>) },
      { id: 'settings', title: 'Settings', icon: 'settings', document: true, tearOff: true, render: () => <SettingsView /> },
      { id: 'ai', title: 'AI', icon: 'sparkles', tearOff: true, minSize: 300, render: () => <AiPanel /> },
      { id: 'history', title: 'Undo History', icon: 'undo', tearOff: true, minSize: 200, render: () => <HistoryPanel /> },
      { id: 'build', title: WEBSITE ? 'Publish' : 'Build', icon: 'layers', tearOff: true, minSize: 320, render: () => (WEBSITE ? <PublishPanel /> : <BuildWindow />) },
      { id: 'profiler', title: 'Profiler', icon: 'gauge', minSize: 260, render: () => <ProfilerPanel /> },
      { id: 'frameDebugger', title: 'Frame Debugger', icon: 'frames', unmountHidden: true, minSize: 260, render: () => <FrameDebuggerPanel /> },
      { id: 'particles', title: 'Particle Editor', icon: 'particles', tearOff: true, minSize: 280, render: () => <ParticleEditor /> },
      { id: 'uv', title: 'UV Editor', icon: 'grid', minSize: 280, render: () => <UvEditor /> },
      { id: 'graph', title: 'Graph', icon: 'layers', minSize: 360, render: () => <GraphPanel /> },
      { id: 'procgen', title: 'Procedural', icon: 'layers', minSize: 360, render: () => <ProcgenPanel /> },
    ],
    [project.id, project.name],
  );

  // A phone: Prisma, Play and the plan, made for thumbs (same project session).
  if (mobile) return <MobileStudio project={project} />;

  return (
    <div className="workspace">
      <TopBar projectName={project.name} />
      <DockLayout
        state={dock}
        onChange={setDock}
        panels={panels}
        className="workspace-body"
        windowName={`ai-studio-panel-${project.id}`}
        onFocusPanel={(panel) => {
          setFocusedPanel(panel);
          if (DOCUMENT_PANELS.includes(panel) && useWorkspace.getState().view !== panel)
            useWorkspace.getState().setView(panel as WorkspaceView);
        }}
        emptyPrimary={<span className="muted">Every view is docked elsewhere. Pick one from the top bar, or drag a tab here.</span>}
      />
      <ShortcutHelperBar />
      <CommandPalette />
      <ShortcutConflictPrompt />
      <PieMenuHost />
      <QuickFavoritesHost onManage={() => openSearch('search', '>')} />
      {!WEBSITE && <GenerationPrompt />}
    </div>
  );
}

/** Documents keep the old absolutely-filled tab host so their own layouts are unchanged. */
function DocumentHost({ children }: { children: ReactNode }) {
  return <div className="tab-host">{children}</div>;
}

/** A document whose `active` flag (its own key handlers) follows the focused view. */
function ActiveTab({ id }: { id: 'scene' | 'model' | 'pixel' | 'sound' | 'video' }) {
  const active = useWorkspace((s) => s.view === id);
  if (id === 'scene') return <SceneTab active={active} />;
  if (id === 'model') return <ModelTab active={active} />;
  if (id === 'pixel') return <PixelTab active={active} />;
  if (id === 'video') return <VideoTab active={active} />;
  return <SoundTab active={active} />;
}

function PagePanel({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="pane-full">
      <div className="side-head">{label}</div>
      {children}
    </div>
  );
}
